<template>
  <div class="sg">
    <h5 class="sg__title">Size Guide</h5>
    <div v-if="sizeguide.description" class="sg__desc">{{ sizeguide.description }}</div>
    <div class="sg__table-wrapper">
      <span class="sg__table-title">Measurements</span>
      <SizeGuideTable :bodyMeasurements="sizeguide.bodyMeasurements" :fitType="fitType" />
    </div>
    <div class="sg__fit-type" v-for="type in fitTypeData" :key="type.title">
      <span class="sg__fit-type-title">{{ type.title }}</span>
      <span class="sg__fit-type-desc" v-html="type.content"></span>
    </div>
    <div class="sg__accor">
      <div
        class="sg__accor-wrapper"
        data-accor-size
        v-for="section in sizeguide.accordionSections"
        :key="section.title"
      >
        <div
          data-accor-ctrl-size
          class="sg__accor-ctrl"
          role="button"
          tabindex="0"
          :aria-controls="`sg-accor-section-${section.title}`"
          :aria-expanded="false"
          aria-roledescription="This is an accordion in size guide"
        >
          <div class="sg__accor-ctrl-title">{{ section.title }}</div>
          <svg class="sg__accor-ctrl-icon">
            <use href="#icon-next"></use>
          </svg>
        </div>
        <div
          data-accor-target-size
          :id="`sg-accor-section-${section.title}`"
          class="sg__accor-target"
          v-html="section.content"
          :aria-hidden="true"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, toRefs, ref, onMounted } from 'vue';
  import SizeGuideTable from './size-guide-table.vue';

  const props = defineProps({
    sizeguide: {
      type: Object,
      default: () => ({}),
    },

    fitType: {
      type: String,
      default: null,
    },
  });

  const emit = defineEmits(['component-updated']);

  onMounted(() => {
    accorFn();
    iconFn();
    emit('component-updated');
  });

  const { sizeguide, fitType } = toRefs(props);

  const fitTypeData = computed(() => {
    if (fitType.value) {
      return [
        {
          title: 'Fit',
          content: sizeguide.value.fitContentByType[fitType.value],
        },
      ];
    }
  });

  const accorFn = () => {
    const accors = document.querySelectorAll('[data-accor-size]');

    const init = (el) => {
      const ctrl = el.querySelector('[data-accor-ctrl-size]');
      const target = el.querySelector('[data-accor-target-size]');

      toggleAccorHeight(el, target, ctrl);

      const handleAccorInteraction = () => {
        accors.forEach((ele) => {
          const target = ele.querySelector('[data-accor-target-size]');
          target.style.setProperty('--accor-height', `0rem`);
          ele != el && ele.classList.remove('active');
        });

        el.classList.toggle('active');
        toggleAccorHeight(el, target, ctrl);
      };

      const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          handleAccorInteraction();
          event.preventDefault(); // Prevent scrolling with Space key
        }
      };

      ctrl.addEventListener('click', handleAccorInteraction);
      ctrl.addEventListener('keydown', handleKeyDown);
    };

    const toggleAccorHeight = (el, target, ctrl) => {
      if (el.classList.contains('active')) {
        target.style.setProperty('--accor-height', `${target.scrollHeight}px`);
        target.setAttribute('aria-hidden', false);

        ctrl.setAttribute('aria-expanded', true);
      } else {
        target.style.setProperty('--accor-height', `0rem`);
        target.setAttribute('aria-hidden', true);

        ctrl.setAttribute('aria-expanded', false);
      }
    };

    accors.forEach((el) => init(el));
  };

  const iconFn = () => {
    document.querySelectorAll('.callIcon').forEach((el) => {
      el.parentElement.classList.add('sg__iconWrapper');
      el.parentElement.setAttribute('aria-roledescription', 'Contact number');
      el.insertAdjacentHTML(
        'beforebegin',
        `<img class="sg__icon" alt="Call icon" src="https://cdn.shopify.com/s/files/1/0023/9901/0881/files/contact-us-4_30x.jpg?v=1537205239">`
      );
    });
    document.querySelectorAll('.chatIcon').forEach((el) => {
      el.parentElement.classList.add('sg__iconWrapper');
      el.parentElement.setAttribute('aria-roledescription', 'Chat link');
      el.insertAdjacentHTML(
        'beforebegin',
        `<img class="sg__icon" alt="Chat icon" src="https://cdn.shopify.com/s/files/1/0023/9901/0881/files/contact-us-3_30x.jpg?v=1537205256">`
      );
    });
    document.querySelectorAll('.emailIcon').forEach((el) => {
      el.parentElement.classList.add('sg__iconWrapper');
      el.parentElement.setAttribute('aria-roledescription', 'Send an email');
      el.insertAdjacentHTML(
        'beforebegin',
        `<img class="sg__icon" alt="Email icon" src="https://cdn.shopify.com/s/files/1/0023/9901/0881/files/CMS_Icons_-Recovered_30x.jpg?v=1537022422">`
      );
    });
  };
</script>
